import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './W_AudioBar.module.css';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import cn from 'classnames';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';

interface W_AudioBarProps {
  audioUrl: string;
}

export default function W_AudioBar({ audioUrl }: W_AudioBarProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [audioData, setAudioData] = useState<number[]>([]);
  const [waveCount, setWaveCount] = useState(20);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateWaveCount = () => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;
      const count = Math.floor((width - 16) / 6);
      return Math.max(8, Math.min(25, count));
    }
    return 15;
  };

  useEffect(() => {
    const count = calculateWaveCount();
    setWaveCount(count);
    if (audioData.length === 0) {
      generateWaveData(60, count);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      generateWaveData(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      setIsLoading(false);
      setIsPlaying(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        await audio.play();
        setIsPlaying(true);
      }
    } catch {
      setIsPlaying(false);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const progressBar = progressRef.current;
    if (!audio || !progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const newTime = (clickX / width) * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const generateWaveData = useCallback(
    (duration: number, count: number = waveCount) => {
      const waves = Array.from({ length: count }, (_, i) => {
        const baseHeight = Math.random() * 0.3 + 0.1;
        const variation = Math.sin(i * 0.5) * 0.2 + Math.cos(i * 0.3) * 0.1;
        return Math.max(0.05, Math.min(1, baseHeight + variation));
      });
      setAudioData(waves);
    },
    [waveCount]
  );

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  useEffect(() => {
    const handleResize = () => {
      const count = calculateWaveCount();
      setWaveCount(count);
      generateWaveData(duration || 60, count);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, generateWaveData]);

  useEffect(() => {
    if (waveCount > 0) {
      generateWaveData(duration || 60, waveCount);
    }
  }, [waveCount, duration, generateWaveData]);

  return (
    <div ref={containerRef} className={styles.wrapper}>
      <audio ref={audioRef} src={audioUrl} preload='metadata' />

      <div className={styles.controls}>
        <A_CircleButton
          onClick={togglePlayPause}
          icon={
            isPlaying ? (
              <Q_Icon.PauseIcon width={24} height={24} fill='none' />
            ) : (
              <Q_Icon.PlayIcon width={24} height={24} fill='none' />
            )
          }
          variant='tertiary'
        />

        <div className={styles.progress_container}>
          <div
            ref={progressRef}
            className={styles.progress_bar}
            onClick={handleProgressClick}
          >
            <div className={styles.progress_waves}>
              {(audioData.length > 0
                ? audioData
                : Array.from(
                    { length: waveCount },
                    () => Math.random() * 0.5 + 0.2
                  )
              ).map((waveHeight, i) => {
                const isPlayed = (i / waveCount) * 100 <= progressPercentage;
                return (
                  <div
                    key={i}
                    className={styles.wave}
                    style={{
                      height: `${waveHeight * 30 + 5}px`,
                      background: isPlayed
                        ? 'var(--gradient-progress)'
                        : 'var(--gradient-green-primary)',
                    }}
                  />
                );
              })}
            </div>
            <div
              className={styles.progress_fill}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        <span className={cn(styles.duration, 'text-p-7')}>
          {isLoading ? '--:--' : formatTime(duration)}
        </span>
      </div>
    </div>
  );
}
